import { request } from "./http";

const PUSH_SYNC_LS_KEY = "bs:pwa-push-sync";
const PUSH_SUB_PATHS = [
  "/api/me/push-subscriptions",
  "/api/me/web-push-subscriptions",
  "/api/me/pwa/push-subscriptions",
];
const PUSH_SUBSCRIBE_PATHS = [...PUSH_SUB_PATHS, ...PUSH_SUB_PATHS.map((p) => `${p}/subscribe`)];
const PUSH_UNSUBSCRIBE_PATHS = [...PUSH_SUB_PATHS, ...PUSH_SUB_PATHS.map((p) => `${p}/unsubscribe`)];
const FCM_PATHS = [
  "/api/me/fcm-tokens",
  "/api/me/firebase-tokens",
  "/api/me/push-tokens",
];
const FCM_SUBSCRIBE_PATHS = [...FCM_PATHS, ...FCM_PATHS.map((p) => `${p}/subscribe`)];
const FCM_UNSUBSCRIBE_PATHS = [...FCM_PATHS, ...FCM_PATHS.map((p) => `${p}/unsubscribe`)];

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function canFallback(err) {
  const st = err?.status ?? err?.response?.status ?? null;
  return st === 404 || st === 405 || st === 501;
}

function readSyncFallback() {
  try {
    const raw = localStorage.getItem(PUSH_SYNC_LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSyncFallback(value) {
  try {
    localStorage.setItem(PUSH_SYNC_LS_KEY, JSON.stringify(value || {}));
  } catch {}
}

function normalizeSub(subscription) {
  const json = subscription?.toJSON ? subscription.toJSON() : subscription;
  const endpoint = String(json?.endpoint || subscription?.endpoint || "");
  return {
    endpoint,
    subscription: json || null,
    keys: {
      p256dh: json?.keys?.p256dh || "",
      auth: json?.keys?.auth || "",
    },
  };
}

function normalizePushDevice(raw = {}, idx = 0) {
  const item = asObject(raw);
  const channelRaw = String(item.channel || item.type || "").trim().toLowerCase();
  const channel = channelRaw === "fcm" || channelRaw === "firebase" ? "fcm" : "webpush";
  const idRaw = Number(item.id);
  const id = Number.isFinite(idRaw) && idRaw > 0 ? idRaw : null;

  return {
    key: `${channel}:${id != null ? id : idx}`,
    id,
    channel,
    provider: String(item.provider || "").trim(),
    valueMasked: String(item.valueMasked || item.endpointMasked || item.tokenMasked || "").trim(),
    browser: String(item.browser || "").trim(),
    platform: String(item.platform || "").trim(),
    language: String(item.language || "").trim(),
    standalone: Boolean(item.standalone),
    updatedAt: item.updatedAt || item.createdAt || null,
    active: item.active !== false,
  };
}

function normalizePushDevicesResponse(raw = {}) {
  const data = asObject(raw);
  const envelopeItems = asArray(data.items);
  const nestedItems = asArray(data.item?.items);
  const itemsRaw = envelopeItems.length ? envelopeItems : nestedItems;
  const items = itemsRaw.map((x, idx) => normalizePushDevice(x, idx));

  const webPushCount =
    Number.isFinite(Number(data.webPushCount))
      ? Number(data.webPushCount)
      : items.filter((x) => x.channel === "webpush").length;
  const fcmCount =
    Number.isFinite(Number(data.fcmCount))
      ? Number(data.fcmCount)
      : items.filter((x) => x.channel === "fcm").length;

  return {
    ok: data.ok !== false,
    items,
    hasWebPush: typeof data.hasWebPush === "boolean" ? data.hasWebPush : webPushCount > 0,
    hasFcm: typeof data.hasFcm === "boolean" ? data.hasFcm : fcmCount > 0,
    webPushCount,
    fcmCount,
    total: Number.isFinite(Number(data.total)) ? Number(data.total) : items.length,
  };
}

function normalizePushMeta(raw = {}) {
  const data = asObject(raw);
  const vapidPublicKey = String(data.vapidPublicKey || data.publicKey || "").trim();
  const webPushConfigured =
    data.webPushConfigured === true ||
    data.enabled === true ||
    (data.vapidPublicKeyConfigured === true && data.vapidPrivateKeyConfigured === true);

  return {
    ok: data.ok !== false,
    pushEnabled: data.pushEnabled === true || data.enabled === true || webPushConfigured,
    fcmEnabled: data.fcmEnabled === true,
    webPushConfigured,
    vapidPublicKeyConfigured: data.vapidPublicKeyConfigured === true || Boolean(vapidPublicKey),
    vapidPrivateKeyConfigured: data.vapidPrivateKeyConfigured === true,
    vapidPublicKey,
    message: String(data.message || "").trim(),
  };
}

async function tryRequestPaths(paths, optionsFactory) {
  let lastFallbackErr = null;
  for (const path of paths) {
    try {
      return await request(path, optionsFactory(path));
    } catch (err) {
      if (!canFallback(err)) throw err;
      lastFallbackErr = err;
    }
  }
  if (lastFallbackErr) throw lastFallbackErr;
  throw new Error("No push endpoint configured");
}

export const pwaApi = {
  async getPushMeta() {
    const raw = await request("/api/me/push-subscriptions/meta", {
      method: "GET",
    });
    return normalizePushMeta(raw);
  },

  async listPushDevices() {
    const raw = await tryRequestPaths(PUSH_SUB_PATHS, () => ({
      method: "GET",
    }));
    return normalizePushDevicesResponse(raw);
  },

  async disconnectPushDevice(channel, id) {
    const cleanChannel = String(channel || "").trim().toLowerCase();
    const cleanId = Number(id);
    if (!cleanChannel || !Number.isFinite(cleanId) || cleanId <= 0) {
      throw new Error("Некорректный идентификатор устройства");
    }

    const normalizedChannel = cleanChannel === "fcm" || cleanChannel === "firebase" ? "fcm" : "webpush";
    const paths =
      normalizedChannel === "fcm"
        ? [
            `/api/me/push-subscriptions/fcm/${cleanId}`,
            `/api/me/fcm-tokens/${cleanId}`,
            `/api/me/firebase-tokens/${cleanId}`,
            `/api/me/push-tokens/${cleanId}`,
          ]
        : [
            `/api/me/push-subscriptions/webpush/${cleanId}`,
            `/api/me/push-subscriptions/${cleanId}`,
            `/api/me/web-push-subscriptions/${cleanId}`,
            `/api/me/pwa/push-subscriptions/${cleanId}`,
          ];

    return tryRequestPaths(paths, () => ({
      method: "DELETE",
    }));
  },

  async subscribePush(subscription, meta = {}) {
    const normalized = normalizeSub(subscription);
    const payload = {
      endpoint: normalized.endpoint,
      keys: normalized.keys,
      subscription: normalized.subscription,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      language: typeof navigator !== "undefined" ? navigator.language : "",
      platform: String(meta.platform || ""),
      browser: String(meta.browser || ""),
      standalone: Boolean(meta.standalone),
    };

    try {
      const res = await tryRequestPaths(PUSH_SUBSCRIBE_PATHS, () => ({
        method: "POST",
        json: payload,
      }));
      writeSyncFallback({
        endpoint: normalized.endpoint,
        syncedAt: Date.now(),
        method: "POST",
        channel: "webpush",
      });
      return res || { ok: true };
    } catch (err) {
      if (!canFallback(err)) throw err;
      writeSyncFallback({
        endpoint: normalized.endpoint,
        syncedAt: Date.now(),
        method: "POST",
        fallback: true,
        channel: "webpush",
      });
      return { ok: true, fallback: true };
    }
  },

  async unsubscribePush(endpoint) {
    const cleanEndpoint = String(endpoint || "").trim();
    if (!cleanEndpoint) return { ok: true };

    try {
      const res = await tryRequestPaths(PUSH_UNSUBSCRIBE_PATHS, (path) => {
        if (path.endsWith("/unsubscribe")) {
          return {
            method: "POST",
            json: { endpoint: cleanEndpoint },
          };
        }
        return {
          method: "DELETE",
          json: { endpoint: cleanEndpoint },
          headers: {
            "X-Push-Endpoint": cleanEndpoint,
          },
        };
      });
      writeSyncFallback({
        endpoint: cleanEndpoint,
        syncedAt: Date.now(),
        method: "DELETE",
        channel: "webpush",
      });
      return res || { ok: true };
    } catch (err) {
      if (!canFallback(err)) throw err;
      writeSyncFallback({
        endpoint: cleanEndpoint,
        syncedAt: Date.now(),
        method: "DELETE",
        fallback: true,
        channel: "webpush",
      });
      return { ok: true, fallback: true };
    }
  },

  async subscribeFcmToken(token, meta = {}) {
    const cleanToken = String(token || "").trim();
    if (!cleanToken) throw new Error("FCM token is empty");

    const payload = {
      token: cleanToken,
      provider: "FCM",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      language: typeof navigator !== "undefined" ? navigator.language : "",
      platform: String(meta.platform || ""),
      browser: String(meta.browser || ""),
      standalone: Boolean(meta.standalone),
    };

    try {
      const res = await tryRequestPaths(FCM_SUBSCRIBE_PATHS, () => ({
        method: "POST",
        json: payload,
      }));
      writeSyncFallback({
        token: cleanToken,
        syncedAt: Date.now(),
        method: "POST",
        channel: "fcm",
      });
      return res || { ok: true };
    } catch (err) {
      if (!canFallback(err)) throw err;
      writeSyncFallback({
        token: cleanToken,
        syncedAt: Date.now(),
        method: "POST",
        fallback: true,
        channel: "fcm",
      });
      return { ok: true, fallback: true };
    }
  },

  async unsubscribeFcmToken(token) {
    const cleanToken = String(token || "").trim();
    if (!cleanToken) return { ok: true };

    try {
      const res = await tryRequestPaths(FCM_UNSUBSCRIBE_PATHS, (path) => {
        if (path.endsWith("/unsubscribe")) {
          return {
            method: "POST",
            json: { token: cleanToken },
          };
        }
        return {
          method: "DELETE",
          json: { token: cleanToken },
          headers: {
            "X-FCM-Token": cleanToken,
          },
        };
      });
      writeSyncFallback({
        token: cleanToken,
        syncedAt: Date.now(),
        method: "DELETE",
        channel: "fcm",
      });
      return res || { ok: true };
    } catch (err) {
      if (!canFallback(err)) throw err;
      writeSyncFallback({
        token: cleanToken,
        syncedAt: Date.now(),
        method: "DELETE",
        fallback: true,
        channel: "fcm",
      });
      return { ok: true, fallback: true };
    }
  },

  getLastFallbackSync: readSyncFallback,
};
